import i18n from 'i18next';

export interface SportCopy {
  tourDashboard: string;
  tourStudentsTitle: string;
  tourStudents: string;
  tourSchedule: string;
  tourApplications: string;
  tourStaff: string;
  checklistCreateVenue: string;
  checklistCreateStaff: string;
  checklistCreateGroup: string;
  checklistAddStudent: string;
  checklistAssignStudent: string;
  checklistSetupPayment: string;
  mobileTourHome: string;
  mobileTourApplications: string;
  mobileTourStaff: string;
  mobileTourSchedule: string;
  mobileTourStudents: string;
  mobileTourStudentsTitle: string;
  coachTourDashboard: string;
  coachTourStudentsTitle: string;
  coachTourStudents: string;
  coachTourSchedule: string;
  coachTourAttendance: string;
  coachTourVideo: string;
  coachChecklistViewSchedule: string;
  coachChecklistMarkAttendance: string;
  coachChecklistReviewStudents: string;
  coachChecklistUploadVideo: string;
  studentTourDashboard: string;
  studentTourBalance: string;
  studentTourSchedule: string;
  studentTourAttendance: string;
  studentTourRental: string;
  studentChecklistJoinSchool: string;
  studentChecklistViewBalance: string;
  studentChecklistViewSchedule: string;
  studentChecklistConfirmAttendance: string;
  mobileCoachTourShift: string;
  mobileCoachTourGroups: string;
  mobileCoachTourStats: string;
  mobileCoachTourProfile: string;
  mobileStudentTourHome: string;
  mobileStudentTourSchedule: string;
  mobileStudentTourBalance: string;
  mobileStudentTourProfile: string;
}

export interface SportContext {
  venue: string;
  group: string;
  student: string;
  coach: string;
  lesson: string;
  subscription: string;
  venueExample: string;
  groupExample: string;
  studentExample: string;
  scheduleExample: string;
  planExample: string;
  sportEmoji: string;
  venueEmoji: string;
  copy: SportCopy;
}

const tr = i18n.t.bind(i18n) as (key: string) => string;

function buildFallbackCopy(): SportCopy {
  const k = (key: keyof SportCopy) => tr(`onboarding:${key}`);
  return {
    tourDashboard: k('tourDashboard'),
    tourStudentsTitle: k('tourStudentsTitle'),
    tourStudents: k('tourStudents'),
    tourSchedule: k('tourSchedule'),
    tourApplications: k('tourApplications'),
    tourStaff: k('tourStaff'),
    checklistCreateVenue: k('checklistCreateVenue'),
    checklistCreateStaff: k('checklistCreateStaff'),
    checklistCreateGroup: k('checklistCreateGroup'),
    checklistAddStudent: k('checklistAddStudent'),
    checklistAssignStudent: k('checklistAssignStudent'),
    checklistSetupPayment: k('checklistSetupPayment'),
    mobileTourHome: k('mobileTourHome'),
    mobileTourApplications: k('mobileTourApplications'),
    mobileTourStaff: k('mobileTourStaff'),
    mobileTourSchedule: k('mobileTourSchedule'),
    mobileTourStudentsTitle: k('mobileTourStudentsTitle'),
    mobileTourStudents: k('mobileTourStudents'),
    coachTourDashboard: k('coachTourDashboard'),
    coachTourStudentsTitle: k('coachTourStudentsTitle'),
    coachTourStudents: k('coachTourStudents'),
    coachTourSchedule: k('coachTourSchedule'),
    coachTourAttendance: k('coachTourAttendance'),
    coachTourVideo: k('coachTourVideo'),
    coachChecklistViewSchedule: k('coachChecklistViewSchedule'),
    coachChecklistMarkAttendance: k('coachChecklistMarkAttendance'),
    coachChecklistReviewStudents: k('coachChecklistReviewStudents'),
    coachChecklistUploadVideo: k('coachChecklistUploadVideo'),
    studentTourDashboard: k('studentTourDashboard'),
    studentTourBalance: k('studentTourBalance'),
    studentTourSchedule: k('studentTourSchedule'),
    studentTourAttendance: k('studentTourAttendance'),
    studentTourRental: k('studentTourRental'),
    studentChecklistJoinSchool: k('studentChecklistJoinSchool'),
    studentChecklistViewBalance: k('studentChecklistViewBalance'),
    studentChecklistViewSchedule: k('studentChecklistViewSchedule'),
    studentChecklistConfirmAttendance: k('studentChecklistConfirmAttendance'),
    mobileCoachTourShift: k('mobileCoachTourShift'),
    mobileCoachTourGroups: k('mobileCoachTourGroups'),
    mobileCoachTourStats: k('mobileCoachTourStats'),
    mobileCoachTourProfile: k('mobileCoachTourProfile'),
    mobileStudentTourHome: k('mobileStudentTourHome'),
    mobileStudentTourSchedule: k('mobileStudentTourSchedule'),
    mobileStudentTourBalance: k('mobileStudentTourBalance'),
    mobileStudentTourProfile: k('mobileStudentTourProfile')
  };
}

function buildFallbackSportContext(): SportContext {
  return {
    venue: tr('onboarding:venue'),
    group: tr('onboarding:group'),
    student: tr('onboarding:student'),
    coach: tr('onboarding:coach'),
    lesson: tr('onboarding:lesson'),
    subscription: tr('onboarding:subscription'),
    venueExample: tr('onboarding:venueExample'),
    groupExample: tr('onboarding:groupExample'),
    studentExample: tr('onboarding:studentExample'),
    scheduleExample: tr('onboarding:scheduleExample'),
    planExample: tr('onboarding:planExample'),
    sportEmoji: '🏆',
    venueEmoji: '🏟',
    copy: buildFallbackCopy()
  };
}

export let fallbackSportContext = buildFallbackSportContext();

const contextCache = new Map<number, SportContext>();

/** Call on language change so checklist/tour copy uses the new locale. */
export function refreshFallbackSportContext(): void {
  fallbackSportContext = buildFallbackSportContext();
  contextCache.clear();
}

function mapApiContext(data: Record<string, unknown>): SportContext {
  const fb = fallbackSportContext;
  // API copy is Russian-only — tour strings come from i18n (onboarding namespace).
  return {
    venue: String(data.venue ?? fb.venue),
    group: String(data.group ?? fb.group),
    student: String(data.student ?? fb.student),
    coach: String(data.coach ?? fb.coach),
    lesson: String(data.lesson ?? fb.lesson),
    subscription: String(data.subscription ?? fb.subscription),
    venueExample: String(data.venueExample ?? fb.venueExample),
    groupExample: String(data.groupExample ?? fb.groupExample),
    studentExample: String(data.studentExample ?? fb.studentExample),
    scheduleExample: String(data.scheduleExample ?? fb.scheduleExample),
    planExample: String(data.planExample ?? fb.planExample),
    sportEmoji: String(data.sportEmoji ?? fb.sportEmoji),
    venueEmoji: String(data.venueEmoji ?? fb.venueEmoji),
    copy: buildFallbackCopy(),
  };
}

/** Синхронно: из кэша или fallback (после fetchSportContext). */
export function getSportContext(sportTypeId?: number | null): SportContext {
  if (!sportTypeId) return fallbackSportContext;
  return contextCache.get(sportTypeId) ?? fallbackSportContext;
}

/** Загрузка контекста онбординга из API с кэшем. */
export async function fetchSportContext(sportTypeId: number): Promise<SportContext> {
  const cached = contextCache.get(sportTypeId);
  if (cached) return cached;

  const { default: axios } = await import('axios');
  const { getApiBaseUrl } = await import('../apiBase');
  try {
    const res = await axios.get(`${getApiBaseUrl()}/sport-types/${sportTypeId}/context`);
    const ctx = mapApiContext(res.data as Record<string, unknown>);
    contextCache.set(sportTypeId, ctx);
    return ctx;
  } catch {
    return fallbackSportContext;
  }
}

export function cap(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
